/**
 * Home's view model, built only from data the app already stores:
 *   - sessions (useTestingProjects' recentSessions): finished, transcribing,
 *     no audio, summary ready;
 *   - tests: when each was made;
 *   - sites: when each live site last changed (a publish or a rename both set
 *     updatedAt, so the line says "Live at", never "Version N published").
 * Not built here (no data for them yet, product work in PLAN.md): site visits,
 * "Free site goes offline soon", "since you looked" / mark as seen, and blue
 * "new" sessions.
 */
import { buildSiteUrl } from '../../utils/domainUtils';

const DAY = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function timeLabel(d) {
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

/** "Wed 22 Oct" (no comma, as in the v3 design). */
export function dateLabel(d) {
  return `${WD[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
}

export function dayHeading(d, now = new Date()) {
  const diff = Math.round((startOfDay(now) - startOfDay(d)) / DAY);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

export function relativeDay(d, now = new Date()) {
  if (!d) return 'None yet';
  const diff = Math.round((startOfDay(now) - startOfDay(d)) / DAY);
  if (diff <= 0) return `${timeLabel(d)} today`;
  if (diff === 1) return 'Yesterday';
  return dateLabel(d);
}

/** Same rule as UserTestingDashboard's deriveStatus, as a status word. */
export function testStatus(project) {
  const ut = project.userTesting || {};
  if (ut.status === 'completed') return 'closed';
  const target = Number(ut.targetSessions) || 0;
  if (target > 0 && (project.sessionCount || 0) >= target) return 'closed';
  if (ut.status === 'draft') return 'draft';
  if (ut.status) return 'open';
  if ((project.sessionCount || 0) === 0) return 'draft';
  return 'open';
}

const finished = (s) => s.status !== 'in_progress' && s.status !== 'abandoned' && (s.completedAt || (s.status === 'incomplete' && s.startedAt));
const sessionTime = (s) => s.completedAt || s.startedAt;
const transcribing = (s) => s.hasAudio && (s.transcriptStatus === 'pending' || s.transcriptStatus === 'processing');

export function sessionDots(project) {
  return (project.recentSessions || [])
    .filter(finished)
    .slice()
    .reverse()
    .map((s) => (!s.hasAudio ? 'noaudio' : transcribing(s) ? 'processing' : 'done'));
}

export function siteAddress(site) {
  return buildSiteUrl({
    generatedUrl: site.generatedUrl,
    type: site.type,
    customDomain: site.customDomain,
    brand: 'aero',
  }).replace(/^https?:\/\//, '');
}

export function siteIsLive(s) {
  return s.type === 'figma'
    ? !!(s.urls && (s.urls.figmaDesktopUrl || s.urls.figmaMobileUrl))
    : !!s.activeVersion;
}

/**
 * Events, newest first. Each: { key, at, kind, subject, who, text, code, action, to }.
 * `text` is plain; `code` (optional) is an address shown in mono after it.
 */
export function buildEvents({ projects = [], sites = [], limit = 12 }) {
  const out = [];
  projects.filter((p) => !p.draft).forEach((p) => {
    (p.recentSessions || []).filter(finished).forEach((s) => {
      const who = s.testerName || null;
      const name = who || 'A tester';
      const to = `/user-testing/${p.id}/sessions/${s.id}`;
      let kind = 'ses';
      let text = `${name} finished a session.`;
      let action = 'Open';
      if (s.status === 'incomplete') {
        text = `${name} left a session before the end.`;
      }
      if (!s.hasAudio) {
        kind = 'noaud';
        text = `${name} finished a session with no audio, so there is no transcript. You can still watch it.`;
        action = 'Watch';
      } else if (transcribing(s)) {
        kind = 'proc';
        text = `${name} finished a session. The transcript is on its way.`;
      } else if (s.analysisStatus === 'completed') {
        kind = 'review';
        text = `${who ? `${who}'s session` : 'A session'} is ready to review, with its summary.`;
        action = 'Review';
      }
      out.push({ key: `s-${p.id}-${s.id}`, at: sessionTime(s), kind, subject: p.title, who, text, action, to });
    });
    if (p.createdAt) {
      out.push({
        key: `t-${p.id}`,
        at: new Date(p.createdAt),
        kind: 'made',
        subject: p.title,
        text: 'Test made, with its own link.',
        action: 'Open',
        to: `/user-testing/${p.id}`,
      });
    }
  });
  sites.filter((s) => !s.draft && siteIsLive(s)).forEach((s) => {
    const at = s.updatedAt || s.createdAt;
    if (!at) return;
    out.push({
      key: `p-${s.id}`,
      at,
      kind: 'pub',
      subject: s.title || s.generatedUrl,
      text: 'Live at',
      code: siteAddress(s),
      action: 'Open',
      to: `/sites/${s.id}`,
    });
  });
  return out
    .filter((e) => e.at instanceof Date && !Number.isNaN(e.at.getTime()))
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
}

/** Group events under day headings, keeping order. */
export function groupByDay(events, now = new Date()) {
  const groups = [];
  events.forEach((e) => {
    const key = startOfDay(e.at);
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = { key, label: dayHeading(e.at, now), date: dateLabel(e.at), items: [] };
      groups.push(g);
    }
    g.items.push(e);
  });
  return groups;
}
